import {
    VisibilityState,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import useUsersList from '~/services/shop/hooks/mit-fog/useUsersList';
import { Modal } from '~/shared/components/Modal';
import { Text } from '~/shared/components/Text';
import useCanUserAccessFunctionality from '~/shared/utils/mit-fog/canUserAccessFunctionality';
import { Table as UserTable } from '~/templates/blocks/shared/Table/Table';
import { StyledButton } from '../styled';
import AddUsers from './AddUsers';
import DeleteUsers from './DeleteUsers';
import EditUsers from './EditUsers';

const columnHelper = createColumnHelper<ShopTypes.CustomerUserItemDto>();

const select = [10, 50, 100];

export default function Table({
    modal,
    setModal,
}: {
    data?: ShopTypes.CustomerUserItemDto[] | undefined;
    modal: string | undefined;
    currentUser: ShopTypes.UserCustomerResponse | undefined;
    setModal: (modal: 'edit' | 'add' | 'delete' | '') => void;
}) {
    const { translate } = useTranslations();
    const { data } = useUsersList();
    const isUser = useCanUserAccessFunctionality();
    const [user, setUser] = useState<ShopTypes.CustomerUserItemDto | undefined>();
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

    const modalContent: { [key: string]: ReactElement } = {
        edit: (
            <EditUsers handleClose={() => setModal('')} user={user} customerId={data?.customerId} />
        ),
        add: (
            <AddUsers handleClose={() => setModal('')} user={user} customerId={data?.customerId} />
        ),
        delete: (
            <DeleteUsers
                handleClose={() => setModal('')}
                user={user}
                customerId={data?.customerId}
            />
        ),
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('email', {
                header: 'Email',
                cell: (info) => <Text>{info.getValue()}</Text>,
                meta: {
                    isText: true,
                },
                size: 400,
            }),

            columnHelper.accessor('userName', {
                header: 'Navn',
                cell: ({ getValue }) => <Text>{getValue() ? getValue() : '-'}</Text>,
                meta: {
                    isText: true,
                },
                size: 400,
            }),
            columnHelper.accessor('phoneNumber', {
                header: 'Telefon',
                cell: (info) => <Text>{info.getValue()}</Text>,
                meta: {
                    isText: true,
                },
                size: 400,
            }),

            {
                id: 'delete',
                header: '',
                cell: (info: {
                    row: { original: SetStateAction<ShopTypes.CustomerUserItemDto | undefined> };
                }) => {
                    return (
                        <StyledButton
                            variant="link"
                            fullWidth
                            noIcon
                            onClick={() => {
                                setModal('delete');
                                setUser(info.row.original);
                            }}
                        >
                            {translate('mit-fog.userList.delete')}
                        </StyledButton>
                    );
                },
                meta: {
                    isText: true,
                },
                size: 100,
            },
            {
                id: 'edit',
                header: '',
                cell: (info: {
                    row: { original: SetStateAction<ShopTypes.CustomerUserItemDto | undefined> };
                }) => {
                    return (
                        <StyledButton
                            variant="link"
                            noIcon
                            fullWidth
                            onClick={() => {
                                setModal('edit');
                                setUser(info.row.original);
                            }}
                        >
                            {translate('mit-fog.userProfile.edit')}
                        </StyledButton>
                    );
                },
                meta: {
                    isText: true,
                },
                size: 100,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [translate],
    );

    const table = useReactTable({
        data: data?.users ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),

        initialState: {
            pagination: {
                pageSize: select[0],
            },
            sorting: [
                {
                    id: 'email',
                    desc: true,
                },
            ],
        },
        state: {
            columnVisibility,
        },
        onColumnVisibilityChange: setColumnVisibility,
    });

    useEffect(() => {
        table.setColumnVisibility({
            delete: isUser?.isUserAdministrator || false,
            edit: isUser?.isUserAdministrator || false,
        });
    }, [table, isUser?.isUserAdministrator]);

    return (
        <>
            {/* @ts-ignore */}
            <UserTable table={table} data={data?.users ?? []} select={select} />
            <Modal
                isOpen={Boolean(modal)}
                handleClose={() => setModal('')}
                type={modal === 'delete' ? 'miniModal' : 'sidebar'}
            >
                {modal && modalContent[modal]}
            </Modal>
        </>
    );
}
